// The enums of the logistics domain that travel in the contracts: @nexxt/db builds the Postgres enums from these tuples.

/**
 * `stock.atp_waiting_reason` (I-12 §5, ADR-123): why a row of the availability the IMS sent waits instead of entering
 * the snapshot. The warehouse has no mapping from the code of the IMS, or none of the codes of the item is an SKU.
 */
export const atpWaitingReasons = ["unknown_location", "unknown_item"] as const;
export type AtpWaitingReason = (typeof atpWaitingReasons)[number];

/**
 * `logistics.fulfillment_status` (spec v0.2 §5.2, ADR-126): a shipment of a sales order. `ready` from its birth, with
 * its lines; `released_to_3pl` once the instruction left for the warehouse (I-09); `acknowledged` when the warehouse
 * took it (FR-M13-06); `shipped` once the warehouse says it ships nothing more (I-11 `fulfillmentClosed`, ADR-130).
 * `draft` and `document_pending` are not used in F1.
 */
export const fulfillmentStatuses = [
  "draft",
  "ready",
  "document_pending",
  "released_to_3pl",
  "acknowledged",
  "shipped",
  "cancelled",
] as const;
export type FulfillmentStatus = (typeof fulfillmentStatuses)[number];

/**
 * `logistics.shipment_waiting_reason` (I-11 §5, ADR-130): why a shipment the warehouse sent waits instead of being
 * applied, whole. No shipment has the number of the instruction; the shipment is not one the warehouse may ship
 * (never released, cancelled or already closed); the warehouse is not the one of the shipment, or has no mapping;
 * an item none of whose codes is an SKU; an SKU the shipment does not hold; more units than the shipment released
 * (ADR-178 §1.10, ADR-199), which wait for Logistics and the Customer Service instead of passing in silence.
 */
export const shipmentWaitingReasons = [
  "unknown_fulfillment",
  "fulfillment_not_open",
  "location_mismatch",
  "unknown_item",
  "item_not_in_fulfillment",
  "over_released",
] as const;
export type ShipmentWaitingReason = (typeof shipmentWaitingReasons)[number];

/**
 * `logistics.shipment_status` (nexxt.dbml): an outbound shipment the warehouse reported is `in_transit` (I-11); one
 * the Customer Service records by hand (FR-M13-02) is `booked`, or `in_transit` once it says when it left.
 * `delivered` and `exception` come with the tracking of the carrier (F2, I-18).
 */
export const shipmentStatuses = ["planned", "booked", "in_transit", "delivered", "exception", "cancelled"] as const;
export type ShipmentStatus = (typeof shipmentStatuses)[number];

/**
 * `logistics.movement_type` (FR-M12, I-10 §1): the four movements of stock. In F1 `transfer`, between two warehouses
 * of one company, and `transfer_order`, to a warehouse of another company of the group; `vendor_return` and
 * `inventory_adjustment` are F2 (ADR-141).
 */
export const movementTypes = ["inventory_adjustment", "transfer", "transfer_order", "vendor_return"] as const;
export type MovementType = (typeof movementTypes)[number];

/**
 * `logistics.movement_status` (nexxt.dbml, ADR-141): in F1 a movement is `awaiting_confirmation` while the gate of an
 * intercompany transfer waits, `confirmed` once it went to the warehouse and to Business Central (how each took it is
 * its sync, not its status), `executed` when the warehouse closed both its steps (ADR-142), or `cancelled`. An
 * adjustment is `draft` until Logistics confirms it (ADR-226); a vendor return is `requested` until its supplier
 * answers, and `awaiting_confirmation` when it refused, until Operations decides (ADR-227). `validated`,
 * `registered`, `sent_to_3pl` and `error` are not used.
 */
export const movementStatuses = [
  "draft",
  "requested",
  "validated",
  "awaiting_confirmation",
  "confirmed",
  "registered",
  "sent_to_3pl",
  "executed",
  "cancelled",
  "error",
] as const;
export type MovementStatus = (typeof movementStatuses)[number];

/**
 * The statuses of a movement that may still take units out of its warehouse (ADR-141): logistics, asking for another
 * movement, and sales, allocating, subtract from the availability of the IMS the units of its lines that have not left
 * yet, until the warehouse closes the step `shipped` (`unitsLeaving` of @nexxt/db, ADR-142). A vendor return holds
 * its units from when Logistics opens it, while its supplier answers (`requested`, ADR-227).
 */
export const holdingMovementStatuses = [
  "requested",
  "awaiting_confirmation",
  "confirmed",
] as const satisfies readonly MovementStatus[];

/**
 * `logistics.movement_step` (I-11 `kind: movement`, ADR-142): the two steps the warehouse carries a movement out in.
 * `shipped`: the units left the warehouse of origin; `received`: they arrived at the destination. A movement is
 * `executed` once the warehouse closed both, in any order.
 */
export const movementSteps = ["shipped", "received"] as const;
export type MovementStep = (typeof movementSteps)[number];

/**
 * `logistics.movement_execution_waiting_reason` (I-11 §5, ADR-142): why an execution the warehouse sent waits instead
 * of being applied, whole. No movement has its number; the movement is not with the warehouse (waiting for
 * Operations, or cancelled); the warehouse is not the one of the step, or has no mapping; an item none of
 * whose codes is an SKU; an SKU the movement does not move.
 */
export const movementExecutionWaitingReasons = [
  "unknown_movement",
  "movement_not_open",
  "location_mismatch",
  "unknown_item",
  "item_not_in_movement",
] as const;
export type MovementExecutionWaitingReason = (typeof movementExecutionWaitingReasons)[number];

/** The messages a movement goes out with (ADR-100 point 2): to the warehouse (I-10), to Business Central (I-25). */
export const movementMessages = ["I-10", "I-25"] as const;
export type MovementMessage = (typeof movementMessages)[number];

/**
 * `logistics.difference_waiting_reason` (I-11 §5, ADR-226): why a count of the warehouse that does not match the
 * logical stock (I-11 `kind: difference`) waits instead of becoming a proposed adjustment, whole. The warehouse has no
 * mapping from the code of the system it came from; an item none of whose codes is an SKU; two lines of the count name
 * the same SKU.
 */
export const differenceWaitingReasons = ["unknown_location", "unknown_item", "duplicate_item"] as const;
export type DifferenceWaitingReason = (typeof differenceWaitingReasons)[number];

/** Which way an adjustment moves the stock of an SKU (I-25 `inventory_adjustment`): what the warehouse counted, against the IMS. */
export const adjustmentDirections = ["increase", "decrease"] as const;
export type AdjustmentDirection = (typeof adjustmentDirections)[number];

/**
 * The transfers of the F1 (ADR-141), between two warehouses: the movements the feature of the transfers serves. An
 * adjustment (ADR-226) and a vendor return (ADR-227) are movements of one warehouse, each served by its own feature.
 */
export const transferTypes = ["transfer", "transfer_order"] as const satisfies readonly MovementType[];
